/**
 * Antrean Penerimaan — Klasifikasi FAT (spec user 2026-10-22).
 *
 * Menampilkan hasil `GET /api/ar/receipts/queue` dengan filter per klasifikasi
 * kanonis. FAT dapat membuka dialog klasifikasi/split/koreksi via
 * `ReceiptClassifyDialog`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Info, RefreshCw, Shield, ChevronRight } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import ReceiptClassifyDialog from "./ReceiptClassifyDialog";

const CLASS_META = {
  invoice_payment: { label: "Pembayaran Invoice Resmi", cls: "bg-emerald-50 border-emerald-300 text-emerald-800" },
  customer_advance: { label: "DP / Uang Muka", cls: "bg-sky-50 border-sky-300 text-sky-800" },
  retention_payment: { label: "Pembayaran Retensi", cls: "bg-amber-50 border-amber-300 text-amber-800" },
  cross_project_or_misallocated: { label: "Salah Proyek", cls: "bg-rose-50 border-rose-300 text-rose-800" },
  non_ar_receipt: { label: "Non-A.R.", cls: "bg-slate-100 border-slate-300 text-slate-700" },
  duplicate_suspected: { label: "Kemungkinan Duplikat", cls: "bg-rose-50 border-rose-300 text-rose-800" },
  unclassified: { label: "Belum Terklasifikasi", cls: "bg-amber-50 border-amber-400 text-amber-900" },
  verified_overpayment: { label: "Kelebihan Terverifikasi", cls: "bg-indigo-50 border-indigo-300 text-indigo-800" },
};

const FILTER_TABS = [
  ["", "Semua"],
  ["unclassified", "Belum Terklasifikasi"],
  ["invoice_payment", "Pembayaran Resmi"],
  ["customer_advance", "DP"],
  ["retention_payment", "Retensi"],
  ["cross_project_or_misallocated", "Salah Proyek"],
  ["duplicate_suspected", "Duplikat"],
  ["non_ar_receipt", "Non-A.R."],
  ["verified_overpayment", "Kelebihan"],
];

// 2026-10-22 (spec §4): label sumber yang mudah dipahami — bukan nama field mentah.
const SOURCE_LABELS = {
  receipt: "Penerimaan Invoice",
  legacy_implicit: "Legacy — tercatat di invoice",
  project_excess: "Selisih Proyek (legacy)",
  bank_row: "Mutasi Bank",
};

export default function ReceiptClassificationQueue({ initialFilter = "", initialProject = "" }) {
  const [filter, setFilter] = useState(initialFilter);
  const [projectId, setProjectId] = useState(initialProject);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null); // selected item for dialog

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    const q = new URLSearchParams();
    if (filter) q.set("classification", filter);
    if (projectId) q.set("project_id", projectId);
    api.get(`/ar/receipts/queue${q.toString() ? "?" + q.toString() : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat antrean.");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [filter, projectId]);

  useEffect(() => { load(); }, [load]);

  const buckets = data?.buckets || {};
  const items = data?.items || [];

  const bucketTotals = useMemo(() => {
    const b = Object.entries(buckets).map(([k, v]) => ({ k, ...v }));
    return b.filter((x) => x.count > 0 || x.k === filter).sort((a, b) => b.amount - a.amount);
  }, [buckets, filter]);

  return (
    <div className="space-y-3" data-testid="receipt-queue">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-sm uppercase tracking-wider text-[#0B1D3A]">Antrean Penerimaan Perlu Klasifikasi</h2>
        <span className="text-[10px] text-slate-500 basis-full" data-testid="receipt-queue-note">Masuk antrean ini tidak otomatis berarti invoice resmi belum dibayar — artinya penerimaan (termasuk penerimaan legacy) belum dikonfirmasi FAT, sehingga belum dihitung ke progres pembayaran resmi.</span>
        <button onClick={load} disabled={loading} data-testid="receipt-queue-reload"
          className="h-7 px-2.5 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold hover:bg-slate-50 flex items-center gap-1">
          <RefreshCw size={10} className={loading ? "animate-spin" : ""} /> Muat Ulang
        </button>
        {projectId && (
          <span className="text-[10px] text-slate-600" data-testid="receipt-queue-project-filter">
            Proyek: <b>{projectId}</b>
            <button type="button" onClick={() => setProjectId("")} className="ml-1 text-rose-700 hover:underline">× hapus</button>
          </span>
        )}
      </div>

      {err && <div className="border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700" data-testid="receipt-queue-err">{err}</div>}

      {/* Bucket stats */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-1.5" data-testid="receipt-queue-buckets">
          {bucketTotals.map((b) => {
            const meta = CLASS_META[b.k] || { label: b.k, cls: "" };
            return (
              <button key={b.k} type="button" data-testid={`receipt-bucket-${b.k}`}
                onClick={() => setFilter(filter === b.k ? "" : b.k)}
                className={`border p-2 text-left ${filter === b.k ? "border-[#FF5A1F] bg-orange-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                <div className="text-[9px] uppercase tracking-wider text-slate-600 truncate">{meta.label}</div>
                <div className="font-display text-sm tabular-nums text-[#0B1D3A] mt-0.5">{b.count}</div>
                <div className="text-[9px] tabular-nums text-slate-500">{formatIDR(b.amount)}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 flex-wrap">
        {FILTER_TABS.map(([k, l]) => (
          <button key={k || "all"} type="button" onClick={() => setFilter(k)} data-testid={`receipt-filter-${k || "all"}`}
            className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold border-b-2 ${filter === k ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Items table */}
      <div className="border border-slate-200 bg-white overflow-x-auto max-h-[55vh] overflow-y-auto" data-testid="receipt-queue-table">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Tanggal</th>
              <th className="text-left px-3 py-2">Proyek · Client</th>
              <th className="text-left px-3 py-2">Invoice</th>
              <th className="text-left px-3 py-2">Bank · Ref</th>
              <th className="text-right px-3 py-2">Nominal</th>
              <th className="text-left px-3 py-2">Klasifikasi</th>
              <th className="text-left px-3 py-2">Status Verif.</th>
              <th className="text-right px-3 py-2">Umur</th>
              <th className="text-left px-3 py-2">Flag</th>
              <th className="text-right px-3 py-2 pr-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && !items.length && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400 italic">Memuat…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400 italic" data-testid="receipt-queue-empty">Tidak ada penerimaan dalam filter ini.</td></tr>
            )}
            {items.map((it) => {
              const meta = CLASS_META[it.classification] || { label: it.classification, cls: "" };
              const isLegacySrc = it.is_legacy_implicit || it.source_kind === "project_excess";
              return (
                <tr key={it.receipt_key} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`receipt-row-${it.receipt_key}`}>
                  <td className="px-3 py-1.5 tabular-nums text-slate-600">{it.date ? fmtDate(it.date) : <span className="text-slate-400 italic">Belum diketahui</span>}</td>
                  <td className="px-3 py-1.5 text-[11px]">
                    <div className="font-semibold text-[#0B1D3A]">{it.project_name || "—"}</div>
                    <div className="text-[10px] text-slate-500">{it.client || "—"}</div>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[10px]">{it.invoice_number || <span className="text-slate-400 italic font-sans">Belum tertaut</span>}</td>
                  <td className="px-3 py-1.5 text-[10px]">
                    {it.bank_name ? (
                      <>
                        <div>{it.bank_name}{it.account_no ? ` · ${it.account_no}` : ""}</div>
                        <div className="text-slate-400">{it.transfer_ref || "—"}</div>
                      </>
                    ) : (
                      <span className="text-slate-400 italic">Belum tertaut</span>
                    )}
                    <div className="text-[9px] text-slate-400">{SOURCE_LABELS[it.source_kind] || it.source_kind || "Penerimaan"}</div>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{formatIDR(it.amount)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${meta.cls}`}
                      data-testid={`receipt-class-badge-${it.receipt_key}`}>{meta.label}</span>
                    {it.overridden_by_fat && (
                      <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] text-indigo-700" title={`Override FAT: ${it.actor_name || ""}`}>
                        <Shield size={8} /> FAT
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-[10px] text-slate-600">
                    {it.is_legacy_implicit
                      ? <span className="text-amber-700 font-semibold">Perlu Tautan</span>
                      : (it.invoice_review_status === "approved" ? "Resmi" : "Draft")}
                    {it.entry_is_retention_claim && <span className="ml-1 text-amber-700">· Klaim Retensi</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{it.age_days ?? "—"} hari</td>
                  <td className="px-3 py-1.5 text-[9px]">
                    {isLegacySrc && (
                      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-amber-50 border border-amber-300 text-amber-800 font-semibold uppercase tracking-wider"
                        data-testid={`receipt-legacy-badge-${it.receipt_key}`}>Sumber legacy belum tertaut</span>
                    )}
                    {it.is_duplicate_hint && <span className="text-rose-700 inline-flex items-center gap-0.5 ml-1"><AlertTriangle size={9} /> Duplikat</span>}
                    {it.is_cross_project_hint && <span className="text-rose-700 inline-flex items-center gap-0.5 ml-1"><AlertTriangle size={9} /> Lintas</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right pr-3">
                    {data?.can_classify && (
                      <button type="button" data-testid={`receipt-classify-btn-${it.receipt_key}`}
                        onClick={() => setSel(it)}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white">
                        {isLegacySrc ? "Tautkan Sumber" : "Klasifikasi"} <ChevronRight size={10} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-slate-500 italic flex items-start gap-1" data-testid="receipt-queue-footer">
        <Info size={10} className="mt-0.5 shrink-0" />
        Total ditampilkan: <b className="tabular-nums">{formatIDR(data?.total_amount || 0)}</b> ·
        {data?.source || "—"} · Keputusan akhir klasifikasi tetap melalui konfirmasi FAT, tanpa mutasi otomatis data historis.
      </div>

      {sel && (
        <ReceiptClassifyDialog
          item={sel}
          onClose={() => setSel(null)}
          onSaved={() => { setSel(null); load(); toast.success("Klasifikasi tersimpan."); }}
        />
      )}
    </div>
  );
}
